// --- User & Role Types ---

export type UserRole = 'ADMIN' | 'CANDIDATE' | 'LEAD_HAND';

export interface User {
  id: string;
  email: string;
  name: string;
  phoneNumber?: string;
  role?: UserRole;
  orgIds: string[]; // List of organizations this user belongs to
  teamsadminnest?: any; // Nested hierarchy structure from trainingroom - presence indicates admin
  activeOrgId?: string; // Currently selected context
  activeOrgIncludesSubTeams?: boolean; // Whether to include sub-teams in the active context
  lastLogin?: number; // Timestamp
}

export interface Organization {
  id: string;
  name: string;
  description?: string;
  parentId?: string | null;
  createdAt: number;
}

export interface CompetencyRole {
  id: string;
  title: string;
  orgId: string;
}

export interface Message {
  id: string;
  sender: string;
  content: string;
  timestamp: number;
  read: boolean;
}

// --- Pathway Types ---

export type BlockType = 'integration' | 'practical' | 'milestone' | 'upload' | 'affidavit' | 'assessment' | 'coaching' | 'api' | 'pathway' | 'theory' | 'ojt' | 'authorisation';

export interface PathwayBlock {
    id: string;
    name: string;
    type: BlockType;
    description: string;
    duration?: string;
    evidence?: string;
    nextBlockId?: string | 'COMPLETE';
    failureBlockId?: string; // For remedial loops
    branchId?: 'STANDARD' | 'ACCELERATED' | 'REFRESHER' | 'MERGE' | 'REMEDIAL';
    apiEndpoint?: string; // For 'api' type blocks - the endpoint URL for 3rd party to call
    apiProvider?: string; // For 'api' type blocks - name of the external provider (e.g., 'acme_labs')
    horizontalGroup?: string; // Blocks with same horizontalGroup are parallel and can be completed in any order
    horizontalPosition?: number; // Position within the horizontal group (for visual layout)
    linkedPathwayId?: string; // For 'pathway' type blocks - the ID of the sub-pathway to link to
    courseTemplateId?: string; // For 'theory' type blocks - the TrainingRoom course template ID
    courseTemplateName?: string; // Display name of the linked course template
    leadHandIds?: string[]; // Lead hand user IDs for OJT blocks (loaded from pathway)
    ojtConfig?: OjtBlockConfig; // Full OJT block configuration (for 'ojt' type blocks)
    uploadInstructions?: string; // For 'upload' type blocks - instructions shown to the trainee
    practicalConfig?: PracticalBlockConfig;
    assessmentConfig?: AssessmentBlockConfig;
    uploadConfig?: UploadBlockConfig;
    apiConfig?: ApiBlockConfig;
    authorisationConfig?: AuthorisationBlockConfig;
    x?: number; // X-coordinate on visual builder canvas
    y?: number; // Y-coordinate on visual builder canvas
}

/** Parent document in Firestore 'pathways' collection */
export interface PathwayDoc {
    id: string;
    title: string;
    role: string;
    orgId: string;
    status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
    dateCreated: string;
    dateLastUpdated?: string;
    lastUpdatedBy?: string;
    lastEdited: string;
    description?: string;
    soloreadyOutline?: string;
}

/** Version document in Firestore 'pathways/{id}/versions' subcollection */
export interface PathwayVersion {
    id: string;
    pathwayId: string;
    version: number;
    blocks: PathwayBlock[];
    status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
    createdAt?: string;
}

/** Flattened pathway (parent + version merged) used by UI components */
export interface Pathway {
    id: string;
    rootId: string; // ID to group versions of the same pathway
    title: string;
    role: string;
    lastEdited: string;
    status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
    version: number;
    blocks: PathwayBlock[];
    description?: string;
    soloreadyOutline?: string;
}

export interface LevelReviewEntry {
  status: 'submitted' | 'approved' | 'rejected';
  submittedAt?: number;
  reviewedAt?: number;
  reviewedBy?: string;
  reviewedByName?: string;
  rejectionReason?: string;
}

export interface PathwayProgress {
    pathwayId: string;
    pathwayTitle: string;
    candidateId: string;
    candidateName: string;
    progress: number; // 0-100
    status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
    lastActivity: number;
    assignedDate: number;
    selectedTrack?: 'STANDARD' | 'ACCELERATED' | 'REFRESHER' | 'REMEDIAL';
    /**
     * Per-OJT-block starting mode for this candidate's pathway. Map of blockId -> modeId.
     * If a block is not present in this map, the OJT session falls back to the first available mode.
     */
    startingModeByBlockId?: Record<string, string>;
    levelReviews?: Record<string, LevelReviewEntry>;
}

// --- OJT Types ---

export interface OjtElement {
  id: string;
  label: string;
  isCompleted: boolean;
  evidenceUrl?: string; // Photo/Video
}

export interface OjtSession {
  id: string;
  title: string;
  candidateId: string;
  candidateName: string;
  leadHandId: string;
  leadHandName: string;
  pathwayId: string;
  blockId: string;
  orgId?: string;
  date: number; // Scheduled date timestamp
  startTime?: number; // Actual start timestamp
  endTime?: number; // Actual end timestamp
  status: 'SCHEDULED' | 'IN_PROGRESS' | 'REVIEW_PENDING' | 'COMPLETED';
  elements: OjtElement[];
  requiredTimeMinutes: number;
  loggedTimeMinutes: number;
  startSelfieUrl?: string;
  endSignOff?: {
    type: 'SIGNATURE' | 'SELFIE';
    data: string; // Url or Base64 signature
    timestamp: number;
  };
  // --- Extended OJT fields ---
  objectives?: OjtSessionObjective[]; // Runtime objectives with per-objective evidence
  handshakeType?: 'pin' | 'selfie' | 'qr';
  /** PIN issued to the lead hand for this session when handshake/mode-change uses pin method. Persisted so resumed sessions can verify against it. */
  handshakePin?: string;
  handshakeVerified?: boolean;
  preStartChecklistCompleted?: boolean;
  locationLog?: { lat: number; lng: number; timestamp: number }[];
  candidateComments?: { voiceUrl?: string; transcript?: string; timestamp: number }[];
  // --- Review fields ---
  reviewStatus?: 'PENDING' | 'APPROVED' | 'REJECTED';
  reviewedBy?: string;
  reviewedByName?: string;
  reviewedAt?: string;

  objectiveReviews?: OjtObjectiveReview[];
  // --- OJT Modes ---
  /** Mode the session was started in (set on session creation when modes are enabled) */
  startingModeId?: string;
  /** The mode currently active for the session — also matches the open segment in modeTimeLog */
  currentModeId?: string;
  /** Append-only log of time spent in each mode during this session */
  modeTimeLog?: OjtModeTimeSegment[];
}

/** Runtime objective state within an OJT session */
export interface OjtSessionObjective {
  objectiveId: string; // References OjtObjective.id from block config
  label: string;
  isCompleted: boolean;
  evidence?: {
    photos?: string[]; // URLs
    videos?: string[]; // URLs
    voiceRecordings?: { url: string; transcript?: string }[];
    leadHandSignature?: string; // Base64 or URL
  };
}

/** Admin review of a single objective */
export interface OjtObjectiveReview {
  objectiveId: string;
  status: 'SUCCESS' | 'FAILURE';
  failureReason?: string;
  reviewedAt?: string;
}

// --- Activity Feed Types ---

export type ActivityEntityType = 'ADMIN' | 'CANDIDATE' | 'PATHWAY' | 'BLOCK' | 'OJT_SESSION' | 'TEAM';
export type ActivityAction = 'CREATED' | 'UPDATED' | 'DELETED' | 'ASSIGNED' | 'COMPLETED' | 'SIGNED_OFF';

/**
 * Represents a single field change for tracking what was modified.
 * Use this in the `changes` array when logging UPDATED actions.
 */
export interface FieldChange {
  /** The name of the field that changed (e.g., 'name', 'email', 'phoneNumber') */
  field: string;
  /** The previous value before the change */
  from: string;
  /** The new value after the change */
  to: string;
}

/**
 * Metadata for ADMIN entity activities.
 * @example
 * // For UPDATED action - track what fields changed:
 * { changes: [{ field: 'name', from: 'John', to: 'John Smith' }] }
 * // For CREATED action - no metadata needed
 * // For DELETED action - no metadata needed
 */
export interface AdminActivityMetadata {
  /** Array of field changes when action is UPDATED */
  changes?: FieldChange[];
}

/**
 * Metadata for CANDIDATE entity activities.
 * @example
 * // For UPDATED action:
 * { changes: [{ field: 'email', from: 'old@test.com', to: 'new@test.com' }] }
 * // For ASSIGNED action (assigned to pathway):
 * { pathwayName: 'Forklift Operator Training', pathwayId: 'abc123' }
 */
export interface CandidateActivityMetadata {
  /** Array of field changes when action is UPDATED */
  changes?: FieldChange[];
  /** Pathway name when action is ASSIGNED */
  pathwayName?: string;
  /** Pathway ID when action is ASSIGNED */
  pathwayId?: string;
}

/**
 * Metadata for PATHWAY entity activities.
 * @example
 * // For CREATED/UPDATED:
 * { blocksCount: 5 }
 * // For status change:
 * { statusChange: { from: 'DRAFT', to: 'PUBLISHED' } }
 */
export interface PathwayActivityMetadata {
  /** Number of blocks in the pathway */
  blocksCount?: number;
  /** Status change details */
  statusChange?: { from: string; to: string };
}

/**
 * Metadata for BLOCK entity activities.
 * REQUIRED: Always include pathwayName so the activity makes sense in context.
 * @example
 * { pathwayName: 'Forklift Training', blockType: 'practical' }
 */
export interface BlockActivityMetadata {
  /** The pathway this block belongs to - REQUIRED for context */
  pathwayName: string;
  /** The type of block (practical, assessment, etc.) */
  blockType: BlockType;
  /** The pathway ID for navigation */
  pathwayId?: string;
}

/**
 * Metadata for OJT_SESSION entity activities.
 * Include participant names for readable activity messages.
 * @example
 * // For CREATED (scheduled):
 * { candidateName: 'John Doe', leadHandName: 'Michael Ray', scheduledDate: 1706918400000 }
 * // For COMPLETED:
 * { candidateName: 'John Doe', leadHandName: 'Michael Ray', duration: 120 }
 */
export interface OjtSessionActivityMetadata {
  /** Name of the candidate in the session */
  candidateName: string;
  /** Name of the lead hand/supervisor */
  leadHandName: string;
  /** Scheduled date timestamp for CREATED action */
  scheduledDate?: number;
  /** Duration in minutes for COMPLETED action */
  duration?: number;
}

/**
 * Metadata for TEAM entity activities.
 * @example
 * { parentTeamName: 'West Coast Division' }
 */
export interface TeamActivityMetadata {
  /** Parent team name for context */
  parentTeamName?: string;
  /** Change details */
  changes?: FieldChange[];
}

/**
 * Maps each entity type to its required metadata shape.
 * Use this to ensure type-safe activity logging.
 */
export type ActivityMetadataMap = {
  ADMIN: AdminActivityMetadata;
  CANDIDATE: CandidateActivityMetadata;
  PATHWAY: PathwayActivityMetadata;
  BLOCK: BlockActivityMetadata;
  OJT_SESSION: OjtSessionActivityMetadata;
  TEAM: TeamActivityMetadata;
};

/**
 * Activity record stored in Firestore.
 * Each activity represents a single action taken on an entity.
 */
export interface Activity {
  id: string;
  orgId: string;
  entityType: ActivityEntityType;
  action: ActivityAction;
  entityId: string;
  entityName: string;
  performedById: string;
  performedByName: string;
  timestamp: number;
  /** 
   * Contextual data specific to the entity type.
   * See AdminActivityMetadata, CandidateActivityMetadata, etc. for structure.
   * For UPDATED actions, include `changes` array with field-level diffs.
   */
  metadata?: ActivityMetadataMap[ActivityEntityType];
}

// --- Logging Types ---

export type LogLevel = 'DEBUG' | 'INFO' | 'NOTICE' | 'WARNING' | 'ERROR' | 'CRITICAL' | 'ALERT' | 'EMERGENCY';

export interface LogMetadata {
  userId?: string;
  userEmail?: string;
  userName?: string;
  orgId?: string;
  component?: string;
  action?: string;
  duration?: number;
  [key: string]: any; // Allow additional custom fields
}

// --- Practical Block Configuration Types (Context 1: Definition) ---

export interface PracticalRubricItem {
  id: string;
  title: string;
  description: string;
}

export interface PracticalBlockConfig {
  rubric: PracticalRubricItem[];
  authorizedRoles: ('assessor' | 'lead_hand' | 'site_manager')[];
}

// --- Assessment Block Configuration Types (Context 1: Definition) ---

export interface AssessmentQuestion {
  id: string;
  text: string;
  type: 'multiple_choice' | 'short_answer' | 'true_false';
  options?: string[];
  correctAnswer?: string;
}

export interface AssessmentBlockConfig {
  questions: AssessmentQuestion[];
  passingThreshold: number;
  maxAttempts: number;
  requiresManualGrading: boolean;
}

// --- Upload Block Configuration Types (Context 1: Definition) ---

export interface UploadBlockConfig {
  instructions: string;
  acceptedFileTypes: ('image' | 'pdf' | 'document' | 'video')[];
  requiresAdminVerification: boolean;
  aiVerificationEnabled: boolean;
}

// --- API Block Configuration Types (Context 1: Definition) ---

export interface ApiBlockConfig {
  provider: string;
  endpoint: string;
  successCriteria: string;
}

// --- Authorisation Block Configuration Types (Context 1: Definition) ---

export interface AuthorisationBlockConfig {
  authorizedRoles: ('site_manager' | 'operations_director' | 'training_manager' | 'custom')[];
  customRoleLabel?: string;
}

// --- OJT Block Configuration Types (Context 1: Definition) ---

/** Handshake configuration for OJT blocks */
export interface OjtHandshakeConfig {
  pinEnabled: boolean; // Random 4-digit PIN sent to lead hand
  selfieEnabled: boolean; // 2-headed selfie verified by Gemini AI
  qrEnabled: boolean; // QR code displayed on candidate app, scanned by lead hand
}

/** Evidence requirements configurable per objective */
export interface OjtEvidenceConfig {
  photosRequired: number; // 0 = disabled, 1+ = number of photos required
  videosRequired: number; // 0 = disabled, 1+ = number of videos required
  leadHandSignatureRequired: boolean; // Extra signature for mission-critical items
  voiceRecordingEnabled: boolean; // Voice recording with auto-transcript
  links: { title: string; description: string; url: string }[]; // Associated content (YouTube, web)
  associatedTrainingBlockIds: string[]; // IDs of TR prerequisite blocks earlier in pathway
}

/** A single OJT objective defined at block level */
export interface OjtObjective {
  id: string;
  label: string;
  description?: string;
  evidenceConfig: OjtEvidenceConfig;
}

/** Pre-start checklist item (safety/PPE reminders) */
export interface OjtPreStartChecklistItem {
  id: string;
  label: string;
  required: boolean;
}

/** Full OJT block configuration (stored on PathwayBlock.ojtConfig) */
export interface OjtBlockConfig {
  handshake: OjtHandshakeConfig;
  objectives: OjtObjective[];
  preStartChecklist: OjtPreStartChecklistItem[];
  locationTrackingEnabled: boolean; // Track GPS every minute during session
  candidateCommentsEnabled: boolean; // Allow voice comments with transcript
  expectedDurationHours: number; // Hours candidate must log to complete
  /** Available modes for this OJT block. When undefined or empty, modes are disabled. */
  modes?: OjtMode[];
  /** Sign-off configuration applied when the candidate switches modes mid-session. */
  modeSignOff?: OjtModeSignOffConfig;
  // --- Agent-led OJT (beta) — see docs/OJT_AGENT.md ---
  /** Per-block feature flag. When false/undefined, the block runs the existing candidate-led flow unchanged. */
  agentEnabled?: boolean;
  /** Agent persona. v1 ships a single 'coach' persona; typed as a string for forward-compat. */
  agentPersona?: 'coach';
  /** Hard scheduled start time for the agent run (ms epoch). The runtime fires at this exact instant. */
  scheduledStartAt?: number;
  /** How long mini-page links remain valid after issuance. Defaults to 30 in the runtime when unset. */
  miniPageExpiryMinutes?: number;
  /** When true, the agent runs a 2-headed-selfie verification step before SESSION_BODY. */
  requireCoPresenceSelfie?: boolean;
}

// --- Agent-led OJT (beta) — see docs/OJT_AGENT.md ---

/** Phase of an in-flight agent-led OJT run. */
export type OjtAgentPhase =
  | 'SCHEDULED_WAITING'
  | 'READINESS_CHECK'
  | 'SELFIE_VERIFICATION'
  | 'OBJECTIVE_SETTING'
  | 'PRE_START_CHECKLIST'
  | 'SESSION_BODY'
  | 'WRAP_UP'
  | 'SIGN_OFF'
  | 'COMPLETE'
  | 'NO_SHOW'
  | 'FAILED';

/** Live state of a single agent-led OJT session. One doc per OjtSession (sessionId == doc id). */
export interface OjtAgentRun {
  sessionId: string;
  phase: OjtAgentPhase;
  /** Token id of an outstanding mini-page awaiting reply, if any. */
  pendingMinipageToken?: string;
  pendingFromActor?: 'candidate' | 'lead_hand';
  /** When the runtime should escalate / timeout the pending reply (ms epoch). */
  expectedReplyBy?: number;
  retryCount: number;
  /** Updated each time the scheduler ticks this run. */
  lastTickAt: number;
  /** Free-text reason set on terminal FAILED. */
  failureMode?: string;
}

/** Append-only transcript entry for an OJT agent run. */
export interface OjtMessage {
  id: string;
  sessionId: string;
  role: 'agent' | 'candidate' | 'lead_hand' | 'system';
  kind: 'text' | 'minipage_response' | 'tool_call' | 'tool_result' | 'phase_change';
  target?: 'candidate' | 'lead_hand';
  /** Human-readable summary, always populated for transcript rendering. */
  body: string;
  /** Structured payload (mini-page response, tool args / result, etc.). */
  payload?: unknown;
  createdAt: number;
}

/** Mini-page kinds. `free_text` is implemented in v1; the rest are stubbed. */
export type OjtMinipageKind =
  | 'objectives'
  | 'wrap_up'
  | 'free_text'
  | 'selfie'
  | 'sign_off'
  | 'custom';

/** Short-lived token gating access to a single mini-page form. Token is the doc id. */
export interface OjtMinipageToken {
  /** Equal to the Firestore doc id. */
  token: string;
  sessionId: string;
  forActor: 'candidate' | 'lead_hand';
  forUserId: string;
  kind: OjtMinipageKind;
  /** Prompt rendered above the form. */
  prompt: string;
  /** Optional kind-specific schema (e.g. an objectives list to confirm). */
  schema?: unknown;
  expiresAt: number;
  /** Set by submitMinipage when the form is posted. Null/undefined while still consumable. */
  consumedAt?: number;
  /** Submitted payload, mirrored into ojt_messages. */
  response?: unknown;
}

// --- OJT Mode Types ---

/** Stable IDs for the three built-in OJT modes */
export const BUILT_IN_OJT_MODE_IDS = {
  CANDIDATE_SHADOWING: 'candidate-shadowing',
  LEAD_HAND_SHADOWING: 'lead-hand-shadowing',
  SIDE_BY_SIDE: 'side-by-side',
} as const;

/**
 * The three built-in OJT modes used as defaults when an admin enables modes
 * for an OJT block. Custom modes can be added by the admin alongside these.
 */
export const BUILT_IN_OJT_MODES: OjtMode[] = [
  { id: BUILT_IN_OJT_MODE_IDS.CANDIDATE_SHADOWING, label: 'Candidate Shadowing', color: '#f59e0b', isBuiltIn: true },
  { id: BUILT_IN_OJT_MODE_IDS.LEAD_HAND_SHADOWING, label: 'Lead Hand Shadowing', color: '#10b981', isBuiltIn: true },
  { id: BUILT_IN_OJT_MODE_IDS.SIDE_BY_SIDE, label: 'Side-by-Side', color: '#3b82f6', isBuiltIn: true },
];

/** A single OJT mode that the candidate can switch between during a session. */
export interface OjtMode {
  id: string;
  label: string;
  color: string; // hex string, e.g. '#f59e0b'
  /** Optional informational minimum (warning only — does not gate progression) */
  requiredMinutes?: number;
  /** True for the three default modes — used to prevent admins from deleting them */
  isBuiltIn?: boolean;
}

/** Sign-off requirements when the candidate switches modes mid-session. */
export interface OjtModeSignOffConfig {
  required: boolean;
  method: 'pin' | 'signature' | 'selfie' | 'qr';
}

/**
 * One contiguous segment of time spent in a single mode.
 * The most recent segment in OjtSession.modeTimeLog is open (no endTime/durationMinutes)
 * while it is the currently active mode.
 */
export interface OjtModeTimeSegment {
  modeId: string;
  startTime: number; // ms epoch
  endTime?: number; // ms epoch — undefined while the segment is still open
  durationMinutes?: number; // computed at close time
}

// --- TrainingRoom Integration Types ---

/** Course template from TrainingRoom (shared 'coursetemplates' collection) */
export interface CourseTemplate {
  id: string;
  name?: string; // Mapped from courseData.title.original by getCourseTemplates()
  title?: string;
  teams?: string[]; // Team IDs this template belongs to
  adminslist?: string[]; // Admin UIDs who can see this template
  courseData?: {
    title?: { original?: string; [key: string]: any };
    [key: string]: any;
  };
  [key: string]: any; // TR templates may have additional fields
}

/** Course record from TrainingRoom (shared 'course' collection) */
export interface TrainingRoomCourse {
  id: string;
  UID: string; // Candidate user ID
  templateid: string;
  courseid?: string;
  accesscode?: number;
  signeddate?: any; // Firestore Timestamp or date - indicates completion
  duedate?: any; // Firestore Timestamp or date - initial course completion due date
  renewalDate?: any; // Firestore Timestamp or date - renewal/expiry date
  [key: string]: any; // TR courses may have additional fields
}

/** Link between a pathway block and a TrainingRoom course for a specific candidate */
export interface BlockTrainingRoomLink {
  id: string;
  candidateId: string;
  blockId: string;
  pathwayId: string;
  templateId: string;
  courseId?: string;
  accessCode?: number;
  status: 'not_started' | 'in_progress' | 'complete' | 'expired';
  assignedDate?: number;
  completionDate?: number;
  renewalDate?: number;
  metadata?: {
    courseTitle?: string;
    courseUrl?: string;
  };
}

// --- App Section ---
export type AppSection = 'admin' | 'candidate';

// --- Candidate App Navigation ---
export type CandidateViewType = 'my-pathways' | 'pathway-detail' | 'block-detail' | 'ojt-session';

// --- Block Progress (per candidate, per block) ---
export interface BlockProgress {
  id?: string;
  candidateId: string;
  pathwayId: string;
  blockId: string;
  status: 'not_started' | 'in_progress' | 'complete';
  completedAt?: number;
  updatedAt: number;
}

// --- Practical Evaluation (assessor evaluates candidate) ---
export interface PracticalEvaluation {
  id?: string;
  candidateId: string;
  pathwayId: string;
  blockId: string;
  evaluatorId: string;
  evaluatorName: string;
  evaluatorRole: 'assessor' | 'lead_hand' | 'site_manager';
  rubricResults: {
    rubricItemId: string;
    passed: boolean;
    comment?: string;
  }[];
  overallResult: 'pass' | 'fail';
  evidenceUrls?: string[];
  evaluatedAt: number;
}

// --- Assessment Attempt (candidate takes quiz) ---
export interface AssessmentAttempt {
  id?: string;
  candidateId: string;
  pathwayId: string;
  blockId: string;
  attemptNumber: number;
  answers: {
    questionId: string;
    answer: string;
    isCorrect?: boolean;
  }[];
  score?: number;
  passed?: boolean;
  status: 'submitted' | 'graded' | 'pending_review';
  submittedAt: number;
  gradedAt?: number;
  gradedBy?: string;
}

// --- Block Upload (candidate uploads file) ---
export interface BlockUpload {
  id?: string;
  candidateId: string;
  pathwayId: string;
  blockId: string;
  fileUrl: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  uploadedAt: number;
  status: 'pending_review' | 'approved' | 'rejected';
  reviewedBy?: string;
  reviewedAt?: number;
  rejectionReason?: string;
}

// --- API Verification (external API check) ---
export interface ApiVerification {
  id?: string;
  candidateId: string;
  pathwayId: string;
  blockId: string;
  provider: string;
  endpoint: string;
  status: 'pending' | 'success' | 'failure' | 'manual_override';
  responseData?: any;
  verifiedAt?: number;
  overriddenBy?: string;
  overriddenAt?: number;
  errorMessage?: string;
}

// --- Pathway Authorisation (final sign-off) ---
export interface PathwayAuthorisation {
  id?: string;
  candidateId: string;
  pathwayId: string;
  blockId: string;
  authoriserId: string;
  authoriserName: string;
  authoriserRole: string;
  signatureData?: string;
  authorisedAt: number;
  notes?: string;
  pathwayDataReview?: {
    totalBlocks: number;
    completedBlocks: number;
    ojtHoursLogged?: number;
    assessmentScores?: { blockId: string; score: number }[];
  };
}